/**
 * Global logger. Keeps the last MAX_ENTRIES log entries in a ring buffer,
 * persists to AsyncStorage after each write so logs survive crashes.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';

export type LogLevel = 'info' | 'warn' | 'error';

export interface LogEntry {
  id: string;
  level: LogLevel;
  message: string;
  timestamp: string; // ISO-8601
  data?: string;     // JSON-serialised extra data
}

const MAX_ENTRIES = 300;
const STORAGE_KEY = '@lmr_logs';
const entries: LogEntry[] = [];
type Listener = (entry: LogEntry) => void;
const listeners = new Set<Listener>();

function persistAsync(): void {
  // Fire-and-forget — we want every entry on disk before a potential crash
  AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(entries)).catch(() => { /* ignore storage errors */ });
}

function add(level: LogLevel, message: string, data?: unknown): void {
  const entry: LogEntry = {
    id: Date.now().toString(36) + Math.random().toString(36).slice(2),
    level,
    message,
    timestamp: new Date().toISOString(),
    data: data !== undefined ? (() => { try { return JSON.stringify(data, null, 2); } catch { return String(data); } })() : undefined,
  };
  if (entries.length >= MAX_ENTRIES) entries.shift();
  entries.push(entry);
  persistAsync();
  listeners.forEach((cb) => {
    try { cb(entry); } catch { /* listener errors must not propagate */ }
  });
}

export const appLog = {
  info:  (msg: string, data?: unknown) => add('info', msg, data),
  warn:  (msg: string, data?: unknown) => add('warn', msg, data),
  error: (msg: string, data?: unknown) => add('error', msg, data),
  getAll: (): LogEntry[] => [...entries],
  clear: () => {
    entries.length = 0;
    AsyncStorage.removeItem(STORAGE_KEY).catch(() => {});
  },
  /** Call once at app startup to restore logs from the previous session/crash. */
  loadFromStorage: async (): Promise<void> => {
    try {
      const raw = await AsyncStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved: LogEntry[] = JSON.parse(raw);
      if (!Array.isArray(saved)) return;
      // Prepend saved entries (oldest first), avoid duplicates by id
      const existingIds = new Set(entries.map((e) => e.id));
      const toAdd = saved.filter((e) => !existingIds.has(e.id));
      entries.unshift(...toAdd);
      // Trim to MAX_ENTRIES
      while (entries.length > MAX_ENTRIES) entries.shift();
    } catch { /* corrupt storage — ignore */ }
  },
  subscribe: (cb: Listener): (() => void) => {
    listeners.add(cb);
    return () => listeners.delete(cb);
  },
};
